import { uiText } from "@/lib/i18n/uiText";
import { Coins, Infinity as InfinityIcon, Gift, Wallet, Lock } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCreditWallet } from "@/hooks/useCoins";
import { usePendingCredits } from "@/hooks/useCoins";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

/**
 * Compact credit balance indicator for the header. Only shown to signed-in
 * users. Hovering/clicking reveals a breakdown of daily vs other credits.
 * Admins see "Unlimited".
 */
export function CoinBalance({ className }: { className?: string }) {
  const { user, isAdmin } = useAuth();
  const { data: wallet, isLoading } = useCreditWallet();
  const pending = usePendingCredits();

  if (!user) return null;

  if (isAdmin) {
    return (
      <Button
        asChild
        variant="ghost"
        size="sm"
        className={cn("gap-1.5 font-semibold text-gold", className)}
        title={uiText("Admin — unlimited reveals")}
      >
        <Link to="/wallet">
          <InfinityIcon className="h-4 w-4" />
          <span>{uiText("Unlimited")}</span>
        </Link>
      </Button>
    );
  }

  const daily = wallet?.daily_credits ?? 0;
  const purchased = wallet?.purchased_credits ?? 0;
  const earned = wallet?.earned_credits ?? 0;
  const balance = wallet?.balance ?? 0;
  const spendableNow = daily + purchased + earned;
  const pendingTotal = pending.data?.total ?? 0;
  const nextAt = pending.data?.next_available_at;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn("gap-1.5 font-semibold text-gold", className)}
        >
          <Coins className="h-4 w-4" />
          <span className="tabular-nums">{isLoading ? "—" : balance}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{uiText("Credit balance")}</span>
            <span className="text-lg font-bold text-gold tabular-nums">{balance}</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Gift className="h-4 w-4 text-primary" />  {uiText("Monthly credits")} </span>
              <span className="tabular-nums font-medium">{daily}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Wallet className="h-4 w-4 text-primary" />  {uiText("Purchased")} </span>
              <span className="tabular-nums font-medium">{purchased}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Coins className="h-4 w-4 text-primary" />
                 {uiText("Earned")} </span>
              <span className="tabular-nums font-medium">{earned}</span>
            </div>
            {pendingTotal > 0 && (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Lock className="h-4 w-4 text-gold" />  {uiText("Pending")} </span>
                <span className="tabular-nums font-medium">{pendingTotal}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-border/60 pt-2">
              <span className="font-medium">{uiText("Spendable now")}</span>
              <span className="tabular-nums font-bold text-gold">{spendableNow}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {uiText("Purchased credits are always spendable. Monthly credits (5 every 30 days) do not carry over. Earned credits become usable 21 days after you earn them.")}
            {nextAt && pendingTotal > 0 && (
              <> {uiText("Next pending credits usable on")} {new Date(nextAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })}.</>
            )}
          </p>
          <Button asChild size="sm" className="w-full">
            <Link to="/wallet">{uiText("Manage credits")}</Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="w-full">
            <Link to="/rewards">{uiText("See how you earned these")}</Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
